// src/main.js

import { createApp } from 'vue';
import axios from 'axios';
import App from './App.vue';
import vuetify from './plugins/vuetify'; // Import the vuetify instance
import store from './store/store.js'; // Import the Vuex store
import router from './router/router.js'; // Import the router
import { auth } from './firebase';
import { eventService } from './services/EventService'
import okDialog from './plugins/okDialog';

// ---------------------------------------------------------------
// Axios request interceptor — attach Firebase ID token to authenticated calls
// ---------------------------------------------------------------
//
// Two URL prefixes require a Firebase Bearer token:
//
//   /indemnitor/          — the indemnitor service (port 3046). Always
//                           required — every staff-facing route uses staffAuth.
//
//   /portaldb/db/sms/     — the staff-facing SMS routes for the
//                           voicemail-followup feature on portalAPI.
//                           Other portalAPI paths under /portaldb/ remain
//                           unauthenticated for now (a known gap to be
//                           hardened later — extend the URL match below
//                           when those routes get auth).
//
// Firebase Auth restores its session from localStorage asynchronously after
// page load. Components that mount and fire requests before that finishes
// would see auth.currentUser === null and ship requests without a token,
// even when the user is actually signed in. To handle that race we wait
// for the first onAuthStateChanged callback (which fires once the auth
// state has been determined, with either a user or null) before deciding
// whether to attach a token.
//
// Once auth has resolved, auth.currentUser is non-null for signed-in users
// and the wait is skipped — only the first-page-load request pays the cost.
//
// Token retrieval via getIdToken() returns the cached token unless it's
// near expiry, in which case Firebase auto-refreshes.
axios.interceptors.request.use(async (config) => {
  const url = config.url || '';
  const needsToken = url.includes('/indemnitor/')
                  || url.includes('/portaldb/db/sms/');
  if (needsToken) {
    if (!auth.currentUser) {
      await new Promise((resolve) => {
        const unsub = auth.onAuthStateChanged(() => {
          unsub();
          resolve();
        });
      });
    }
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      } catch (err) {
        console.error('[main] Failed to attach Firebase ID token to authenticated request:', err);
      }
    }
  }
  return config;
});

const app = createApp(App);

// Use the Vuetify, Vuex, and Router instances
app.use(vuetify);
app.use(store);
app.use(router); // Use the router
app.use(okDialog);

// Make eventService available globally
app.provide('eventService', eventService)

// Check localStorage for existing user data
let storedUser = localStorage.getItem('user');
if (storedUser) {
  console.log('User found in local storage');
  storedUser = JSON.parse(storedUser);
  store.commit('setUser', storedUser);
} else {
  console.log('No user found in local storage.');
}

// Check localStorage for existing user data
let storedSelectedUser = localStorage.getItem('selectedUser');
if (storedSelectedUser) {
  console.log('selectedUser found in local storage');
  storedSelectedUser = JSON.parse(storedSelectedUser);
  store.commit('setSelectedUser', storedSelectedUser); // Initialize Vuex with the stored user
} else {
  console.log('No selectedUser found in local storage.');
}

// Check localStorage for existing user data
let storedSelectedCompany = localStorage.getItem('selectedCompany');
if (storedSelectedCompany) {
  console.log('storedSelectedCompany found in local storage');
  storedSelectedCompany = JSON.parse(storedSelectedCompany);
  store.commit('setSelectedCompany', storedSelectedCompany); // Initialize Vuex with the stored user
} else {
  console.log('No selectedCompany found in local storage.');
}

// firebase
auth.onAuthStateChanged(async (authUser) => {
  if (authUser) {
    store.commit('updateUser', {
      email: authUser.email,
      uid: authUser.uid,
    });
  } else {
    console.log('main: onAuthStateChange logout');
    store.dispatch('logout');
  }
});

app.mount('#app');

